import React, { useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { ErrorType, styled } from '@workday/canvas-kit-react/common';
import { Popper, useCloseOnEscape, useCloseOnOutsideClick, usePopupModel, useReturnFocus, useTransferOnFullscreenExit, } from '@workday/canvas-kit-react/popup';
import { colors, borderRadius, inputColors } from '@workday/canvas-kit-react/tokens';
import { buttonBorderWidth } from './SelectBase';
export const menuAnimationDuration = 200;
const menuBorderStyles = (theme, error) => {
    let borderColor = theme.canvas.palette.common.focusOutline;
    let dividerBorderColor = borderColor;
    let dividerBorderWidth = 1;
    if (error === ErrorType.Error) {
        borderColor = theme.canvas.palette.error.main;
        dividerBorderColor = borderColor;
    }
    else if (error === ErrorType.Alert) {
        borderColor = theme.canvas.palette.alert.darkest;
        dividerBorderColor = theme.canvas.palette.alert.main;
        dividerBorderWidth = 2;
    }
    const dividerBorder = `${dividerBorderWidth}px solid ${dividerBorderColor}`;
    return {
        borderColor: borderColor,
        // Render the divider using a pseudo-element
        '&:before': {
            backgroundColor: colors.soap400,
            borderLeft: dividerBorder,
            borderRight: dividerBorder,
            boxSizing: 'border-box',
            content: '""',
            display: 'block',
            height: 1,
            position: 'absolute',
            width: '100%',
            '[data-popper-placement="bottom"] &': {
                top: 0,
            },
            '[data-popper-placement="top"] &': {
                bottom: 0,
            },
        },
    };
};
const menuListBorderStyles = (theme, error) => {
    let borderColor = theme.canvas.palette.common.focusOutline;
    let borderWidth = 1;
    if (error === ErrorType.Error) {
        borderColor = theme.canvas.palette.error.main;
    }
    else if (error === ErrorType.Alert) {
        borderColor = theme.canvas.palette.alert.main;
        borderWidth = 2;
    }
    const border = `${borderWidth}px solid ${borderColor}`;
    return {
        borderLeft: border,
        borderRight: border,
        '[data-popper-placement="bottom"] &': {
            borderBottom: border,
        },
        '[data-popper-placement="top"] &': {
            borderTop: border,
        },
    };
};
const Menu = styled('div')({
    backgroundColor: colors.frenchVanilla100,
    border: `1px solid ${inputColors.border}`,
    boxSizing: 'border-box',
    position: 'relative',
    transition: `opacity ${menuAnimationDuration}ms`,
    '[data-popper-placement="bottom"] &': {
        borderRadius: `0 0 ${borderRadius.m} ${borderRadius.m}`,
        borderTop: 0,
    },
    '[data-popper-placement="top"] &': {
        borderRadius: `${borderRadius.m} ${borderRadius.m} 0 0`,
        borderBottom: 0,
    },
}, ({ error, theme }) => ({
    ...menuBorderStyles(theme, error),
}), ({ visibility }) => ({
    opacity: visibility === 'opening' || visibility === 'opened' || visibility === 'close' ? 1 : 0,
}), ({ width }) => ({
    width: width,
}));
const MenuList = styled('ul')({
    listStyle: 'none',
    margin: 0,
    maxHeight: 200,
    outline: 'none',
    overflowY: 'auto',
    padding: 0,
}, ({ error, theme }) => ({
    ...menuListBorderStyles(theme, error),
}));
const generatePopperOptions = (props) => {
    const { menuRef, placement, shouldAutoFlip, shouldAutoFocus } = props;
    let fallbackPlacements = [];
    if (shouldAutoFlip) {
        fallbackPlacements = placement === 'top' ? ['bottom'] : ['top'];
    }
    const modifiers = [
        {
            name: 'flip',
            options: {
                fallbackPlacements,
            },
        },
        {
            name: 'offset',
            options: {
                offset: () => {
                    const skidding = 0;
                    // Displace menu towards the button to obscure the bottom
                    // edge of the button and to create a smooth visual
                    // connection between the button and the menu
                    const distance = -parseInt(borderRadius.m, 10);
                    return [skidding, distance];
                },
            },
        },
        {
            name: 'preventOverflow',
            options: {
                // Ensure the menu stays aligned with its reference (button),
                // even if that means the menu is pushed out of view
                mainAxis: false,
            },
        },
        {
            // Disable the fallbackModifier as SelectMenu is properly handled by the
            // flip modifier through shouldAutoFlip prop
            name: 'fallbackModifier',
            enabled: false,
        },
    ];
    return {
        modifiers,
        // TODO: Consider using a more general-purpose focus function here rather
        // than relying on Popper's onFirstUpdate for better control over how
        // focus is managed
        onFirstUpdate: () => {
            if (shouldAutoFocus && menuRef && menuRef.current) {
                menuRef.current.focus();
            }
        },
    };
};
export const SelectMenu = ({ buttonRef, children, error, menuRef, onClose, placement = 'bottom', shouldAutoFlip = true, shouldAutoFocus = true, visibility = 'closed', ...elemProps }) => {
    const model = usePopupModel({
        initialVisibility: 'visible',
        returnFocusRef: buttonRef,
        onHide: onClose,
    });
    const [width, setWidth] = useState(0);
    const handleWidthChange = useCallback(() => {
        if (buttonRef && buttonRef.current && visibility !== 'closed') {
            const newMenuWidth = buttonRef.current.clientWidth + 2 * buttonBorderWidth;
            setWidth(newMenuWidth);
        }
    }, [buttonRef, visibility]);
    useLayoutEffect(() => {
        handleWidthChange();
    }, [handleWidthChange]);
    // TODO: Figure out a better way to handle width changes in the reference button.
    // Seems like we should resize the menu when the reference button width changes, not
    // necessarily when the window resizes. Resizing the menu on window resize addresses
    // the case when `grow = true` and the user resizes the browser window while the
    // menu is opened, but doesn't address cases where the reference button size changes
    // through other means.
    useEffect(() => {
        // Update menu width state on resize to ensure menu resizes as window resizes
        window.addEventListener('resize', handleWidthChange);
        return () => {
            window.removeEventListener('resize', handleWidthChange);
        };
    }, [handleWidthChange]);
    useCloseOnEscape(model);
    useCloseOnOutsideClick(model);
    useReturnFocus(model);
    useTransferOnFullscreenExit(model);
    return (React.createElement(Popper, { placement: placement, anchorElement: buttonRef, popperOptions: generatePopperOptions({
            menuRef,
            placement,
            shouldAutoFlip,
            shouldAutoFocus,
        }), ref: model.state.stackRef },
        React.createElement(Menu, { error: error, visibility: visibility, width: width },
            React.createElement(MenuList, Object.assign({ error: error, ref: menuRef, role: "listbox", tabIndex: -1 }, elemProps), children))));
};
